import { useMemo } from 'react';
import type { MKEERAApiResponseItem } from '../dashboard.types';
import { formatExcelDate, isValidCoordinates, parseAmount, parseCoordinate } from '../utils/dataProcessing';

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  address: string;
  payeeType?: string;
  amountOfPayment?: number;
  typeOfAssistance?: string;
  dateOfPayment?: string;
  gender?: string;
  contactName?: string;
  contactEmail?: string;
}

export function useMapMarkers(data: MKEERAApiResponseItem[]): MapMarker[] {
  return useMemo(() => {
    // Safety check: ensure data is an array
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    return data
      .filter((item) => {
        const appData = item.applicationData;
        return isValidCoordinates(appData.lat || '', appData.long || '');
      })
      .map((item, index): MapMarker | null => {
        const appData = item.applicationData;
        const lat = parseCoordinate(appData.lat || '');
        const lng = parseCoordinate(appData.long || '');

        if (lat === null || lng === null) {
          return null;
        }

        return {
          id: item.id || index.toString(),
          lat,
          lng,
          address: [appData.address1, appData.addressLine2, appData.addressLine3, appData.cityName, appData.stateCode, appData.zip5]
            .filter(Boolean)
            .join(', '),
          payeeType: appData.payeeType,
          amountOfPayment: parseAmount(appData.amountOfPayment),
          typeOfAssistance: appData.typeOfAssistance,
          dateOfPayment: formatExcelDate(appData.dateOfPayment),
          gender: appData.gender,
          contactName: item.contactName,
          contactEmail: item.contactEmail,
        };
      })
      .filter((marker): marker is MapMarker => marker !== null);
  }, [data]);
}
